import { css, Global } from '@emotion/react';
import { MouseEventHandler, ReactNode, Ref } from 'react';
import { IMaskInput } from 'react-imask';
import { NavProps } from 'react-day-picker';
import { format } from 'date-fns';
import { ClearIcon, Icon, IconProps, Input } from '@anytag/shared-atoms';
import { DATE_PICKER_INPUT_MASK_FORMAT, THEME } from '@anytag/shared-constants';
import { cn } from '@anytag/shared-utils';
import { parseDate } from './helpers';

export const WithGlobalStyles = ({ children }: { children: ReactNode }) => (
  <>
    <Global
      styles={css({
        // https://react-day-picker.js.org/basics/styling
        '.rdp-root': {
          '.rdp-day': {
            fontSize: '12px'
          },
          '.rdp-selected': {
            fontSize: '12px',
            fontWeight: 'normal'
          },
          '.rdp-selected:not(.rdp-range_middle)': {
            fontSize: '12px',
            fontWeight: 'normal',

            '&.rdp-focused > .rdp-day_button:': {
              backgroundColor: THEME.datePicker.colors.active.background,
              color: THEME.datePicker.colors.active.text
            },
            // when we open the picker, the selected day may not have focused class
            '> button[aria-label$=", selected"]': {
              backgroundColor: THEME.datePicker.colors.active.background,
              color: THEME.datePicker.colors.active.text
            }
          },
          '.rdp-range_end': {
            '> .rdp-day_button': {
              borderRadius: '0 12px 12px 0',
              fontSize: '12px',
              fontWeight: 'normal'
            }
          },
          '.rdp-range_start': {
            '> .rdp-day_button': {
              borderRadius: '12px 0 0 12px',
              fontSize: '12px',
              fontWeight: 'normal'
            }
          },
          '--rdp-day-width': '32px' /* Width of the day cells. */,
          '--rdp-day-height': '32px' /* Height of the day cells. */,
          '--rdp-day_button-height': '32px' /* Height of the day cells button. */,
          '--rdp-day_button-width': '32px' /* Width of the day cells button. */,
          '--rdp-caption_label': '14px' /* Font size for the caption labels. */,
          '--rdp-accent-color':
            THEME.datePicker.colors.active.background /* Accent color for the background of selected days. */,
          '--rdp-accent-background-color':
            THEME.datePicker.colors.hover.background /* Background color for the hovered/focused elements. */,
          '--rdp-outline': 'none' /* Outline border for focused elements */,
          '--rdp-outline-selected': 'none' /* Outline border for focused _and_ selected elements */,
          margin: '20px 16px 12px' /* Root container's margin */
        },
        '.rdp-month_caption': {
          height: '22px',
          marginBottom: '8px',
          justifyContent: 'center',
          '.rdp-caption_label': {
            fontSize: '14px',
            fontWeight: 'normal'
          }
        },

        '.rdp-range_middle': {
          color: THEME.datePicker.colors.range.text,
          background: THEME.datePicker.colors.range.background,
          '&:hover': { backgroundColor: THEME.datePicker.colors.hover.background }
        }
      })}
    />
    {children}
  </>
);
export interface CustomInputProps {
  hasError: boolean;
  disabled: boolean;
  clearable?: boolean;
  onClear: () => void;
  onClick: () => void;
  value: string;
  ref?: Ref<HTMLInputElement>;
  onAcceptCallback?: (parsedDate: Date) => void;
  className?: string;
}

export const CustomInput = (props: CustomInputProps) => {
  const {
    hasError,
    disabled,
    clearable = true,
    onClear,
    ref,
    value,
    onAcceptCallback,
    onClick,
    className,
    ...rest
  } = props;

  return (
    <div ref={ref} onClick={onClick} className={cn('grid h-8', className)}>
      <Icon
        id="calendar-icon"
        icon="calendar"
        size="16px"
        tabIndex={-1}
        variant="gray"
        className="z-0 col-[1] row-[1] ml-2 place-self-start self-center"
      />
      {onAcceptCallback ? (
        <StyledInputWithMask
          value={value ? format(value, DATE_PICKER_INPUT_MASK_FORMAT) : ''}
          onAcceptCallback={onAcceptCallback}
        />
      ) : (
        <Input
          readOnly
          className="col-[1] row-[1] px-7 focus:border-base-border-hover active:border-base-border"
          type="text"
          disabled={disabled}
          value={value}
          {...rest}
        />
      )}
      {!!value && clearable && !disabled ? (
        <ClearIcon
          onClick={onClear}
          className="col-[1] row-[1] mr-1 place-self-end self-center hover:icon-color-gray"
        />
      ) : null}
    </div>
  );
};

interface StyledInputWithMaskProps {
  className?: string;
  value: string;
  pattern?: string;
  placeholder?: string;
  onAcceptCallback: (parsedDate: Date) => void;
}
export const StyledInputWithMask = ({
  value,
  className,
  onAcceptCallback,
  pattern = 'Y/`m/`dd',
  placeholder = 'yyyy/mm/dd'
}: StyledInputWithMaskProps) => (
  // @ts-expect-error it looks type issue of IMask, mask={undefined} is OK but mask={Date || undefined} is not
  <IMaskInput
    className={cn(
      'w-19 cursor-pointer text-size-input hover:shadow-container',
      'placeholder:text-black-50 placeholder:text-sm placeholder:font-normal',
      className
    )}
    mask={value ? Date : undefined}
    value={value}
    pattern={pattern} // Pattern mask with defined blocks, default is 'd{.}`m{.}`Y'
    onAccept={
      // depending on prop above first argument is
      // `value` if `unmask=false`,
      // `unmaskedValue` if `unmask=true`,
      // `typedValue` if `unmask='typed'`
      (currentValue) => {
        const parsedDate = parseDate(currentValue, DATE_PICKER_INPUT_MASK_FORMAT);

        if (parsedDate) {
          onAcceptCallback(parsedDate);
        }
      }
    }
    placeholder={placeholder}
  />
);

interface ArrowIconProps extends Pick<IconProps, 'icon'> {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: 'previous' | 'next';
}
export const ArrowIcon = ({ onClick, icon, type = 'next' }: ArrowIconProps) => (
  <div
    className={cn(
      'absolute top-0 flex h-5 w-5 cursor-pointer items-center justify-center rounded-xs p-0.5 hover:outline hover:outline-black-40',
      type === 'next' ? 'right-1' : 'left-1'
    )}
  >
    <Icon
      icon={icon}
      size="10px"
      variant="gray"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e as any);
      }}
    />
  </div>
);

export const CustomDayPickerNav = (props: NavProps) => (
  <>
    <ArrowIcon onClick={props.onPreviousClick} icon="caret-left" type="previous" />
    <ArrowIcon onClick={props.onNextClick} icon="caret-right" type="next" />
  </>
);
